/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, ElementRef, EventEmitter, Output, ViewChild } from "@angular/core";
import { Zaa_AssetDto } from "app/models/zoneassignedassets/Zaa_AssetDto";
import { ZoneAssignedAssetsService } from "app/services/zone-assigned-assets.service";
import { TypeaheadMatch } from "ngx-bootstrap/typeahead";
import { from, Observable } from "rxjs";

/** Declare $ to call bootstrap */
declare var $: any;

/**
 * Dialog component used to allow the User to select an Asset and pass the value
 * to the parent component (if one was selected).
 */
@Component({
  selector: 'ef-add-asset-dialog',
    templateUrl: './add-asset-dialog.component.html',
    styleUrls: [
      '../../../../assets/css/infrastructure.css'
    ]
  })
export class AddAssetDialogComponent {

  /** Output used to pass the selected Asset to the parent component */
  @Output()
  assetSelected = new EventEmitter<Zaa_AssetDto>();

  /** Asset that was selected by the User */
  selectedAsset: Zaa_AssetDto | null = null;

  /** Assets that are available for the User to select from */
  availableAssets$?: Observable<Zaa_AssetDto[]>;

  /** Reference to the HTML Input used for the Asset Name (needed to clear any values entered) */
  @ViewChild('assetInput') assetInputElement!: ElementRef<HTMLInputElement>;


  /** Constructor */
  constructor(
    private zaasService: ZoneAssignedAssetsService
  ) { }


  /** 
   * Shows the Dialog  
   */
  public show(): void {
    this.assetInputElement.nativeElement.value = '';
    this.selectedAsset = null;
    this.availableAssets$ = from(this.searchAssetsByName(""));
    $("#addAssetDialog").modal("show");
  }

  /**
   * Hides the Dialog
   */
  public hide(): void {
    this.assetInputElement.nativeElement.value = '';
    this.selectedAsset = null;
    $("#addAssetDialog").modal("hide");
  }

  /**
   * Called when the "Cancel" UI Button is clicked.
   * Cancels / Hides the Dialog
   */
  onCancelClicked(): void {
    this.hide();
  }

  /**
   * Called when the "Add" UI Button is clicked.
   * Hides the Dialog and emits the selected Asset to the parent.
   */
  onAddClicked(): void {
    if (this.selectedAsset != null) {
      this.assetSelected.emit(this.selectedAsset);
      this.hide();
    }
  }

  /**
   * Called when the User types in the "Asset" input.
   * Refreshes the available assets presented to the User in the drop down.
   */
  onInputChanged(value: string): void {
    this.availableAssets$ = from(this.searchAssetsByName(value));
  }

  /**
   * Called when the User clicks on an available asset option in the UI.
   * Sets the clicked on asset to the one that was selected.
   */
  onAssetSelected(event: TypeaheadMatch): void {
    this.selectedAsset = event.item;
  }

  /**
   * Returns the avaiable asset options using the given name as the search criteria. 
   */
  private async searchAssetsByName(name: string): Promise<Zaa_AssetDto[]> {
    const rv = await this.zaasService.searchForUnassignedAssets(0, 20, name);
    return rv.data;
  }

}
